import { useEffect, useRef, useState } from 'react';

import { GAME_MODE, SingSetup, SongPreview } from '~/interfaces';
import { Calibration } from '~/modules/calibration/calibration';
import { Menu } from '~/modules/elements/akui/menu';
import { useBackground } from '~/modules/elements/background-context';
import Modal from '~/modules/elements/modal';
import { VideoState } from '~/modules/elements/video-player/index';
import GameState from '~/modules/game-engine/game-state/game-state';
import events from '~/modules/game-events/game-events';
import useBlockScroll from '~/modules/hooks/use-block-scroll';
import useFullscreen from '~/modules/hooks/use-fullscreen';
import useViewportSize from '~/modules/hooks/use-viewport-size';
import PlayersManager from '~/modules/players/players-manager';
import useSong from '~/modules/songs/hooks/use-song';
import { CalibrationIntro } from '~/routes/game/singing/calibration-intro';
import useCalibrationBeforeFirstSong from '~/routes/game/singing/use-calibration-before-first-song';
import WaitForReadiness from '~/routes/game/singing/wait-for-readiness';
import LayoutGame from '~/routes/layout-game';
import { IsCalibratedSetting, useSettingValue } from '~/routes/settings/settings-state';

import Player, { PlayerRef } from './player';
import PostGame from './post-game/post-game';

interface Props {
  singSetup: SingSetup;
  songPreview: SongPreview;
  returnToSongSelection: () => void;
  restartSong: () => void;
}
function Singing({ songPreview, singSetup, returnToSongSelection, restartSong }: Props) {
  useFullscreen();
  useBlockScroll();
  const player = useRef<PlayerRef | null>(null);
  const song = useSong(songPreview.id, {
    sourceType: songPreview.sourceType,
    sharedSongId: songPreview.sharedSongId,
  });
  const [isCalibrated, setIsCalibrated] = useSettingValue(IsCalibratedSetting);

  const { width, height } = useViewportSize();
  const [isEnded, setIsEnded] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [showCalibrationIntro, setShowCalibrationIntro] = useState(true);
  const [playerState, setPlayerState] = useState(VideoState.UNSTARTED);

  const [isTransitionTimeout, setIsTransitionTimeout] = useState(false);

  useBackground(!isTransitionTimeout);

  const calibrationGate = useCalibrationBeforeFirstSong(isCalibrated);
  const showCalibration = calibrationGate === 'show';
  const readyToSing = calibrationGate === 'hide';
  useEffect(() => {
    if (readyToSing && isOverlayVisible && song.data && (isTransitionTimeout || playerState !== VideoState.UNSTARTED)) {
      setIsOverlayVisible(false);
    }
  }, [song.data, isTransitionTimeout, playerState, isOverlayVisible, readyToSing]);

  if (isEnded && song.data) {
    return (
      <PostGame
        width={width}
        height={height}
        song={song.data}
        onClickSongSelection={returnToSongSelection}
        singSetup={singSetup}
      />
    );
  } else {
    return (
      <LayoutGame>
        <div className="relative">
          <div
            className={`z-scene-overlay pointer-events-none fixed inset-0 flex h-full w-full flex-col items-stretch px-10 py-10 transition-opacity duration-500 [view-transition-name:song-preview] ${
              isOverlayVisible ? 'opacity-100' : 'opacity-0'
            }`}
            data-test="background-container">
            <div
              className="absolute inset-0 scale-105 bg-black bg-cover bg-center blur-[10px]"
              style={{
                backgroundImage: `url('https://i3.ytimg.com/vi/${songPreview.video}/hqdefault.jpg')`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            />
            {/* These sit directly on the song's own thumbnail, so they need `text-shadow-legible`
                to survive a bright frame. */}
            <span
              className="typography text-shadow-legible text-2xl [view-transition-name:song-preview-artist] 2xl:text-3xl"
              data-test="song-artist">
              {songPreview.artist}
            </span>
            <span
              className="typography text-active text-shadow-legible text-3xl [view-transition-name:song-preview-title] 2xl:text-5xl"
              data-test="song-title">
              {songPreview.title}
            </span>
            <Modal open={showCalibration}>
              {showCalibration && (
                <Menu modal>
                  {showCalibrationIntro ? (
                    <CalibrationIntro onContinue={() => setShowCalibrationIntro(false)} />
                  ) : (
                    <Calibration source="song" onSave={() => setIsCalibrated(true)} />
                  )}
                </Menu>
              )}
            </Modal>
            {readyToSing && (
              <WaitForReadiness
                onFinish={() => {
                  setIsTransitionTimeout(true);
                  player.current?.play();
                }}
              />
            )}
          </div>
          {song.data && readyToSing && (
            <Player
              pauseMenu
              ref={player}
              onStatusChange={setPlayerState}
              song={song.data}
              width={width}
              height={height}
              autoplay={false}
              onSongEnd={() => {
                const scores =
                  GameState.getSingSetup()?.mode === GAME_MODE.CO_OP
                    ? [
                        {
                          name: PlayersManager.getPlayers()
                            .map((player) => player.getName())
                            .join(', '),
                          score: GameState.getPlayerScore(0),
                        },
                      ]
                    : PlayersManager.getPlayers().map((player) => ({
                        name: player.getName(),
                        score: GameState.getPlayerScore(player.number),
                      }));
                const progress = GameState.getSongCompletionProgress();
                events.songEnded.dispatch(song.data!, singSetup, scores, progress);
                setIsEnded(true);
              }}
              singSetup={singSetup}
              restartSong={restartSong}
            />
          )}
        </div>
      </LayoutGame>
    );
  }
}

export default Singing;
